import { Avatar, Flex, Modal, Space, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { Icon } from '~/features/shared/icons'
import { MixpanelClient } from '~/plugins/mixpanel/client'
import { GenericCopyFlow, Platform } from './components/GenericFlow'
import { WordpressFlow } from './components/WordpressFlow'

type Props = {
  blogPostId: string
  content: string
  onClose: () => void
  onPublish: (platform: string) => void
}

const PLATFORMS: Platform[] = [
  'Wix',
  'WordPress',
  'Squarespace',
  'Markdown',
  'HTML',
]

const PLATFORM_META: Record<
  Platform,
  { icon: React.ReactNode; backgroundColor: string }
> = {
  Wix: {
    icon: <Icon name="wix" />,
    backgroundColor: '#f6c708',
  },
  WordPress: {
    icon: <Icon name="wordpress" />,
    backgroundColor: '#21759b',
  },
  Squarespace: {
    icon: <Icon name="squarespace" />,
    backgroundColor: '#000000',
  },
  Markdown: {
    icon: <Icon name="markdown" />,
    backgroundColor: '#555555',
  },
  HTML: {
    icon: <Icon name="html" />,
    backgroundColor: '#e44d26',
  },
}

export const ModalPublish: React.FC<Props> = ({
  blogPostId,
  content,
  onClose,
  onPublish,
}) => {
  const [platformSelected, setPlatformSelected] = useState<Platform | null>(
    null,
  )
  const mixpanel = MixpanelClient.use()

  useEffect(() => {
    mixpanel.track('sales.publish.opened')
  }, [])

  const renderPlatformFlow = () => {
    if (!platformSelected) return null

    if (platformSelected === 'WordPress') {
      return (
        <WordpressFlow
          blogPostId={blogPostId}
          onBack={() => setPlatformSelected(null)}
          onPublish={onPublish}
        />
      )
    }

    return (
      <GenericCopyFlow
        content={content}
        platform={platformSelected}
        onBack={() => setPlatformSelected(null)}
        onPublish={onPublish}
      />
    )
  }

  return (
    <Modal title="Publish to a Platform" open onCancel={onClose} footer={null}>
      {platformSelected ? (
        renderPlatformFlow()
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            Select a platform to publish your content
          </Typography.Text>
          <Flex
            gap={24}
            wrap={false}
            style={{ overflowX: 'auto', flexWrap: 'nowrap' }}
          >
            {PLATFORMS.map(platform => (
              <Flex
                key={platform}
                vertical
                align="center"
                gap={8}
                style={{ cursor: 'pointer', width: 80 }}
                onClick={() => setPlatformSelected(platform)}
              >
                <Avatar
                  size={64}
                  icon={PLATFORM_META[platform].icon}
                  style={{
                    backgroundColor: PLATFORM_META[platform].backgroundColor,
                    color: 'white',
                  }}
                />
                <Typography.Text
                  type="secondary"
                  style={{
                    textAlign: 'center',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {platform}
                </Typography.Text>
              </Flex>
            ))}
          </Flex>
        </Space>
      )}
    </Modal>
  )
}
